import React from 'react';
import { cn } from "@/lib/utils";
import { Marquee } from "./marquee";
import { ClientTweetCard } from "./ClientTweetCard";

// Sample tweet IDs - you can replace these with your actual tweet IDs
const sampleTweets = [
  "1750138139702911483", // Startup Archive tweet
  "1688364994386419712", // Cory's tweet
  "1926361983957262539",  // Garry Tan's tweet
  "1850610764467544558", // Sam Altmaan Tweet
 "1853499799078850845", // AirBnB Tweet

];

// Create arrays for different rows
const firstRow = sampleTweets;
const secondRow = [...sampleTweets].reverse();

// Simple wrapper component for the tweet card
const TweetCardWrapper = ({ id }: { id: string }) => {
  return (
    <div className={cn(
      "relative h-64 w-full cursor-pointer overflow-hidden rounded-xl border p-4 my-2",
      // light styles
      "border-gray-950/[.1] bg-gray-950/[.01] hover:bg-gray-950/[.05]",
      // dark styles
      "dark:border-gray-50/[.1] dark:bg-gray-50/[.10] dark:hover:bg-gray-50/[.15]",
    )}>
      <ClientTweetCard id={id} />
    </div>
  );
};

export function TweetMarquee() {
  return (
    <div className="relative flex h-screen w-full items-center justify-center overflow-hidden bg-background">
      {/* Container for the 4 columns */}
      <div className="flex h-full w-full">
        {/* First column - moves down */}
        <div className="h-full w-1/4 px-2">
          <Marquee vertical pauseOnHover={false} className="[--duration:30s]">
            {firstRow.map((tweetId) => (
              <div key={`col1-${tweetId}`} className="my-2">
                <TweetCardWrapper id={tweetId} />
              </div>
            ))}
          </Marquee>
        </div>

        {/* Second column - moves up */}
        <div className="h-full w-1/4 px-2">
          <Marquee vertical reverse pauseOnHover={false} className="[--duration:25s]">
            {secondRow.map((tweetId) => (
              <div key={`col2-${tweetId}`} className="my-2">
                <TweetCardWrapper id={tweetId} />
              </div>
            ))}
          </Marquee>
        </div>

        {/* Third column - moves down */}
        <div className="h-full w-1/4 px-2">
          <Marquee vertical pauseOnHover={false} className="[--duration:35s]">
            {firstRow.map((tweetId) => (
              <div key={`col3-${tweetId}`} className="my-2">
                <TweetCardWrapper id={tweetId} />
              </div>
            ))}
          </Marquee>
        </div>

        {/* Fourth column - moves up */}
        <div className="h-full w-1/4 px-2">
          <Marquee vertical reverse pauseOnHover={false} className="[--duration:28s]">
            {secondRow.map((tweetId) => (
              <div key={`col4-${tweetId}`} className="my-2">
                <TweetCardWrapper id={tweetId} />
              </div>
            ))}
          </Marquee>
        </div>
      </div>

      {/* Gradient overlays */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1/4 bg-gradient-to-b from-background to-transparent"></div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-background to-transparent"></div>
      <div className="pointer-events-none absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-background to-transparent"></div>
      <div className="pointer-events-none absolute inset-y-0 right-0 w-1/4 bg-gradient-to-l from-background to-transparent"></div>
    </div>
  );
}

// 3D version with vertical scrolling
export function TweetMarquee3D() {
  return (
    <div className="relative flex h-screen w-full items-center justify-center overflow-hidden bg-background">
      <div 
        className="relative h-full w-full"
        style={{
          perspective: '1000px',
          transformStyle: 'preserve-3d',
        }}
      >
        {/* First column - moves down */}
        <div 
          className="absolute top-0 bottom-0 left-1/4 -translate-x-1/2 transform"
          style={{
            transform: 'translateZ(100px) rotateY(10deg)',
          }}
        >
          <Marquee 
            pauseOnHover={false} 
            vertical={true}
            className="[--duration:30s]"
          >
            {firstRow.map((tweetId) => (
              <div key={`first-${tweetId}`} className="my-4">
                <TweetCardWrapper id={tweetId} />
              </div>
            ))}
          </Marquee>
        </div>

        {/* Second column - moves up */}
        <div 
          className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 transform"
          style={{
            transform: 'translateZ(0) rotateY(-10deg)',
          }}
        >
          <Marquee 
            reverse 
            pauseOnHover={false} 
            vertical={true}
            className="[--duration:30s]"
          >
            {secondRow.map((tweetId) => (
              <div key={`second-${tweetId}`} className="my-4">
                <TweetCardWrapper id={tweetId} />
              </div>
            ))}
          </Marquee>
        </div>

        {/* Third column - moves down */}
        <div 
          className="absolute top-0 bottom-0 left-3/4 -translate-x-1/2 transform"
          style={{
            transform: 'translateZ(-100px) rotateY(10deg)',
            opacity: 0.8,
          }}
        >
          <Marquee 
            pauseOnHover={false} 
            vertical={true}
            className="[--duration:30s]"
          >
            {firstRow.map((tweetId) => (
              <div key={`third-${tweetId}`} className="my-4">
                <TweetCardWrapper id={tweetId} />
              </div>
            ))}
          </Marquee>
        </div>
      </div>

      {/* Gradient overlays */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1/4 bg-gradient-to-b from-background to-transparent"></div>
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-background to-transparent"></div>
      <div className="pointer-events-none absolute inset-y-0 left-0 w-1/4 bg-gradient-to-r from-background to-transparent"></div>
      <div className="pointer-events-none absolute inset-y-0 right-0 w-1/4 bg-gradient-to-l from-background to-transparent"></div>
    </div>
  );
}
